import type { DeleteDialogOptions } from "../hooks/useDeleteDialog";

/** How far the work on an item got — what a delete would erase. `null` when nothing happened yet. */
export type RecordedWork = "completed" | "started" | null;

type Noun = "Termin" | "Aufgabe";

/** "Dieser Termin" / "Diese Aufgabe" — German takes a different article per noun. */
const THIS: Record<Noun, string> = { Termin: "Dieser Termin", Aufgabe: "Diese Aufgabe" };
const OTHERS: Record<Noun, string> = { Termin: "Termine", Aufgabe: "Aufgaben" };

/**
 * What the confirm dialog says before a Termin or Aufgabe is removed.
 *
 * The office may delete finished work, so the dialog is where it learns what goes with it: a
 * completed or begun item carries Ist-Zeiten and the technician's feedback, and none of it can be
 * brought back. That text is red (`tone: "warning"`); a plain delete keeps the quiet grey line.
 *
 * In a series the two buttons are different acts, and only one of them is gentle: "Diesen und alle
 * folgenden" leaves later items that already have recorded work alone (the API skips them), which
 * the dialog says so nobody expects them gone.
 */
export function buildDeleteDialogOptions({
  noun,
  recorded,
  isSeries,
}: {
  noun: Noun;
  recorded: RecordedWork;
  isSeries: boolean;
}): DeleteDialogOptions {
  const lines: string[] = [];

  if (recorded === "completed") {
    lines.push(
      `${THIS[noun]} ist bereits abgeschlossen. Ist-Zeiten, Abschlussdaten und Rückmeldungen des Handwerkers werden endgültig gelöscht.`,
    );
  } else if (recorded === "started") {
    lines.push(
      `${THIS[noun]} wurde bereits begonnen. Die erfassten Ist-Zeiten werden endgültig gelöscht.`,
    );
  }

  if (isSeries) {
    lines.push(`${THIS[noun]} gehört zu einer Serie.`);
    if (recorded) {
      lines.push(
        `Bei „Diesen und alle folgenden“ bleiben spätere ${OTHERS[noun]} mit erfassten Zeiten erhalten.`,
      );
    }
  }

  return {
    isSeries,
    description: lines.length > 0 ? lines.join(" ") : undefined,
    tone: recorded ? "warning" : undefined,
  };
}
